import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FinancialFilters = ({ onFiltersChange }) => {
  const [filters, setFilters] = useState({
    dateRange: '30d',
    currency: 'USD',
    department: 'all',
    metric: 'all'
  });
  const [isOpen, setIsOpen] = useState(false);

  const dateRangeOptions = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '1y', label: 'Last Year' },
    { value: 'ytd', label: 'Year to Date' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const currencyOptions = [
    { value: 'USD', label: 'USD ($)', symbol: '$' },
    { value: 'EUR', label: 'EUR (€)', symbol: '€' },
    { value: 'GBP', label: 'GBP (£)', symbol: '£' },
    { value: 'JPY', label: 'JPY (¥)', symbol: '¥' },
    { value: 'CAD', label: 'CAD (C$)', symbol: 'C$' }
  ];

  const departmentOptions = [
    { value: 'all', label: 'All Departments' },
    { value: 'sales', label: 'Sales' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'operations', label: 'Operations' },
    { value: 'rd', label: 'R&D' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'finance', label: 'Finance' },
    { value: 'it', label: 'Information Technology' }
  ];

  const metricOptions = [
    { value: 'all', label: 'All Metrics' },
    { value: 'revenue', label: 'Revenue Metrics' },
    { value: 'profitability', label: 'Profitability' },
    { value: 'cashflow', label: 'Cash Flow' },
    { value: 'budget', label: 'Budget Analysis' },
    { value: 'costs', label: 'Cost Analysis' },
    { value: 'roi', label: 'ROI Metrics' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      dateRange: '30d',
      currency: 'USD',
      department: 'all',
      metric: 'all'
    };
    setFilters(defaultFilters);
    onFiltersChange?.(defaultFilters);
  };

  const getActiveFilterCount = () => {
    return Object.values(filters)?.filter(value => 
      value !== 'all' && value !== '30d' && value !== 'USD'
    )?.length;
  };

  return (
    <div className="relative">
      <Button
        variant="outline"
        iconName="Filter"
        onClick={() => setIsOpen(!isOpen)}
        className={`${isOpen ? 'bg-primary text-primary-foreground' : ''}`}
      >
        Filters
        {getActiveFilterCount() > 0 && (
          <span className="ml-1 bg-primary text-primary-foreground rounded-full w-5 h-5 text-xs flex items-center justify-center">
            {getActiveFilterCount()}
          </span>
        )}
      </Button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-lg card-shadow z-50">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Financial Filters</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-muted rounded-md"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            </div>
            
            <div className="p-4 space-y-6">
              {/* Date Range */}
              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">
                  <Icon name="Calendar" size={16} className="inline mr-2" />
                  Date Range
                </label>
                <div className="space-y-2">
                  {dateRangeOptions?.map((option) => (
                    <label key={option?.value} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="dateRange"
                        value={option?.value}
                        checked={filters?.dateRange === option?.value}
                        onChange={(e) => handleFilterChange('dateRange', e?.target?.value)}
                        className="w-4 h-4 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-foreground">{option?.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Currency */}
              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">
                  <Icon name="DollarSign" size={16} className="inline mr-2" />
                  Currency
                </label>
                <select
                  value={filters?.currency}
                  onChange={(e) => handleFilterChange('currency', e?.target?.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {currencyOptions?.map((option) => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Department */}
              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">
                  <Icon name="Building" size={16} className="inline mr-2" />
                  Department
                </label>
                <select
                  value={filters?.department}
                  onChange={(e) => handleFilterChange('department', e?.target?.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {departmentOptions?.map((option) => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Metric Type */}
              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">
                  <Icon name="BarChart3" size={16} className="inline mr-2" />
                  Metric Type
                </label>
                <select
                  value={filters?.metric}
                  onChange={(e) => handleFilterChange('metric', e?.target?.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {metricOptions?.map((option) => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="p-4 border-t border-border flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                Reset All
              </Button>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" onClick={() => setIsOpen(false)}>
                  Cancel
                </Button>
                <Button variant="default" size="sm" onClick={() => setIsOpen(false)}>
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FinancialFilters;